import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { collection, getDocs, doc, getDoc, deleteDoc } from 'firebase/firestore';
import {
  Box, Container, Typography, Button, Paper, Avatar,
  CircularProgress, Chip, IconButton, Alert, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Preview';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddDisabledIcon from '@mui/icons-material/PersonAddDisabled';

const ALLOWED_ROLES = ['superadmin', 'admin', 'gruppledare', 'anbudsansvarig'];

export default function SubcontractorList() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [subcontractors, setSubcontractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    async function load() {
      const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
      const role = userSnap.data()?.role || '';
      if (!ALLOWED_ROLES.includes(role)) {
        setLoading(false);
        return;
      }
      setAuthorized(true);
      const snap = await getDocs(collection(db, 'subcontractors'));
      setSubcontractors(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }
    load();
  }, [currentUser]);

  async function handleDelete() {
    await deleteDoc(doc(db, 'subcontractors', deleteTarget.id));
    setSubcontractors(prev => prev.filter(s => s.id !== deleteTarget.id));
    setDeleteTarget(null);
  }

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  if (!authorized) return (
    <Box sx={{ p: 4 }}>
      <Alert severity="error">Du har inte behörighet att se denna sida.</Alert>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ backgroundColor: '#1a1a1a', color: 'white', py: 2, px: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LagbasLogo />
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
            <IconButton color="inherit" onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PersonAddDisabledIcon />
              <Typography variant="h5" fontWeight="bold">Underkonsulter</Typography>
            </Box>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />}
            sx={{ bgcolor: 'white', color: '#1a1a1a' }}
            onClick={() => navigate('/subcontractors/new')}>
            Ny underkonsult
          </Button>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 3 }}>
        {subcontractors.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <PersonAddDisabledIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Inga underkonsulter registrerade ännu
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Skapa profiler för externa konsulter som kan ingå i anbud.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />}
              onClick={() => navigate('/subcontractors/new')}>
              Skapa första underkonsulten
            </Button>
          </Paper>
        ) : (
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {subcontractors.length} underkonsult{subcontractors.length !== 1 ? 'er' : ''} registrerad{subcontractors.length !== 1 ? 'e' : ''}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {subcontractors.map(sub => (
              <Box key={sub.id} sx={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', p: 2,
                border: '1px solid', borderColor: 'divider',
                borderRadius: 2, mb: 1.5,
                bgcolor: 'background.default'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar src={sub.personal?.photo} sx={{ width: 48, height: 48 }}>
                    {sub.personal?.name?.charAt(0) || '?'}
                  </Avatar>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontWeight="bold">{sub.personal?.name}</Typography>
                      <Chip label="Underkonsult" size="small"
                        sx={{ bgcolor: '#FF6B00', color: 'white', fontSize: 11 }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {sub.personal?.title}
                      {sub.personal?.company ? ` · ${sub.personal.company}` : ''}
                    </Typography>
                    {sub.competencies?.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                        {sub.competencies.slice(0, 4).map((c, i) => (
                          <Chip key={i} label={c} size="small" variant="outlined"
                            sx={{ fontSize: 11 }} />
                        ))}
                        {sub.competencies.length > 4 && (
                          <Chip label={`+${sub.competencies.length - 4}`}
                            size="small" variant="outlined" sx={{ fontSize: 11 }} />
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton size="small"
                    onClick={() => navigate(`/subcontractors/${sub.id}/preview`)}
                    title="Förhandsgranska CV">
                    <PreviewIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small"
                    onClick={() => navigate(`/subcontractors/${sub.id}`)}
                    title="Redigera profil">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error"
                    onClick={() => setDeleteTarget(sub)}
                    title="Ta bort">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Paper>
        )}
      </Container>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Ta bort underkonsult</DialogTitle>
        <DialogContent>
          <Typography>
            Är du säker på att du vill ta bort{' '}
            <strong>{deleteTarget?.personal?.name}</strong>?
            Åtgärden kan inte ångras.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Avbryt</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Ta bort
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
